import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

function TermsPage() {
  return (
    <div className="bg-slate-800 text-white py-16 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Terms of Service — CommunityGuardAPI</title>
        <link rel="canonical" href="https://communityguardapi.com/terms" />
        <meta name="description" content="Terms of service for using the CommunityGuardAPI sex offender registry data API." />
        <meta property="og:title" content="Terms of Service — CommunityGuardAPI" />
        <meta property="og:description" content="Terms of service for using the CommunityGuardAPI sex offender registry data API." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/terms" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-4 text-emerald-500">Terms of Service</h1>
        <p className="text-center text-slate-400 mb-12">Last Updated: March 2026</p>

        <div className="prose prose-invert prose-lg max-w-none text-slate-300">
          <p className="text-xl text-slate-200 mb-8">
            Welcome to CommunityGuardAPI. By accessing or using our website, API, or any associated
            services (collectively, the "Service"), you agree to be bound by these Terms of Service
            ("Terms"). If you do not agree to these Terms, you must not use the Service.
          </p>

          {/* Section 1 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">1. Service Description</h2>
            <p>
              CommunityGuardAPI, operated by Asan Digital LLC ("we," "us," or "our"), is a sex offender
              registry data API and search platform that aggregates publicly available sex offender registry data
              from individual state registries and the National Sex Offender Public Website (NSOPW).
              The Service is provided for <strong>informational purposes only</strong> and is intended
              to help individuals, organizations, and developers access standardized registry
              information from across the United States.
            </p>
          </div>

          {/* Section 2 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">2. Data Accuracy Disclaimer</h2>
            <p>
              All data presented through the Service is sourced from publicly available government
              registries and is refreshed on an approximately monthly basis. While we strive to
              provide accurate and up-to-date information, <strong>we do not guarantee the accuracy,
              completeness, or timeliness of any data</strong> displayed on the platform or returned
              through the API.
            </p>
            <p className="mt-4">
              Registry data is subject to change without notice due to factors including, but not
              limited to, address changes, reclassifications, legal proceedings, expungements, and
              administrative corrections by state agencies.
            </p>
            <p className="mt-4 bg-amber-900/30 border border-amber-600/40 rounded-lg p-4 text-amber-200">
              <strong>Important:</strong> Users must independently verify all information through
              official state registries and NSOPW before taking any action based on data obtained
              from this Service. Reliance on information provided by CommunityGuardAPI without
              independent verification is done solely at your own risk.
            </p>
          </div>

          {/* Section 3 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">3. No Substitute for Official Sources</h2>
            <p>
              CommunityGuardAPI is designed to supplement, not replace, official sex offender registries
              maintained by state and federal agencies. The Service should not be used as the sole
              source of information for any purpose, including but not limited to employment
              screening, housing decisions, or personal safety assessments.
            </p>
            <p className="mt-4">
              For the most current and authoritative information, users should always consult{' '}
              <a
                href="https://www.nsopw.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-500 hover:text-amber-400"
              >
                nsopw.gov
              </a>{' '}
              and the official sex offender registry website of the relevant state or jurisdiction.
            </p>
          </div>

          {/* Section 4 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">4. Prohibited Uses</h2>
            <p>
              The information provided through CommunityGuardAPI is made available solely for lawful
              purposes. The following uses are <strong>strictly prohibited</strong>:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2 mt-4">
              <li>
                Using information obtained from the Service to harass, threaten, intimidate, stalk,
                or commit any criminal act against any individual listed in the registry.
              </li>
              <li>
                Engaging in vigilantism, retaliation, or any form of extrajudicial action against
                registered individuals.
              </li>
              <li>
                Distributing registry data in a manner intended to incite violence, discrimination,
                or harassment.
              </li>
              <li>
                Using the Service to facilitate discrimination in housing, employment, or public
                accommodations in violation of applicable law.
              </li>
            </ul>
            <p className="mt-4 bg-red-900/30 border border-red-600/40 rounded-lg p-4 text-red-200">
              <strong>Warning:</strong> Using sex offender registry information to commit crimes
              against registered individuals is <strong>illegal</strong> under federal and state law.
              Violations of this provision may be reported to law enforcement authorities.
            </p>
          </div>

          {/* Section 5 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">5. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ASAN DIGITAL LLC AND ITS OFFICERS,
              DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, AND LICENSORS SHALL NOT BE LIABLE FOR ANY
              DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT
              OF OR RELATED TO YOUR USE OF, OR INABILITY TO USE, THE SERVICE.
            </p>
            <p className="mt-4">
              This limitation of liability applies to, but is not limited to, damages arising from:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2 mt-4">
              <li>Outdated, incomplete, or inaccurate registry information</li>
              <li>Missing or omitted records</li>
              <li>Incorrect addresses or location data</li>
              <li>Errors in risk level classifications or offense categorizations</li>
              <li>Delays in data refresh cycles</li>
              <li>Service interruptions, downtime, or technical errors</li>
              <li>Actions taken by third parties based on information obtained from the Service</li>
            </ul>
            <p className="mt-4">
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of
              any kind, whether express or implied, including but not limited to implied warranties
              of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </div>

          {/* Section 6 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">6. API Terms of Use</h2>
            <p>
              Access to the CommunityGuardAPI is subject to the following additional terms:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2 mt-4">
              <li>
                <strong>Rate Limits:</strong> API usage is subject to rate limits based on your
                subscription tier. Exceeding rate limits may result in temporary suspension of API
                access.
              </li>
              <li>
                <strong>Usage-Based Pricing:</strong> API access beyond free-tier allowances is
                billed according to the pricing schedule published on our{' '}
                <Link to="/pricing" className="text-emerald-500 hover:text-amber-400">
                  Pricing page
                </Link>.
              </li>
              <li>
                <strong>API Key Security:</strong> API keys are issued to individual accounts and
                must not be shared, published, or embedded in client-side code. You are responsible
                for all activity conducted under your API key.
              </li>
              <li>
                <strong>Prohibited Activity:</strong> Automated bulk scraping, systematic
                downloading, or redistribution of the entire dataset is strictly prohibited.
              </li>
              <li>
                <strong>Attribution:</strong> Applications using the CommunityGuardAPI should
                provide appropriate attribution and include a disclaimer that data is sourced from
                public registries and may not be current.
              </li>
            </ul>
            <p className="mt-4">
              We reserve the right to revoke API access at any time for violations of these terms or
              for any activity that we determine, in our sole discretion, to be abusive or harmful.
            </p>
          </div>

          {/* Section 7 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">7. Data Refresh Policy</h2>
            <p>
              Registry data is refreshed approximately monthly from official state sex offender
              registries and NSOPW. The refresh schedule may vary by state depending on data
              availability and technical constraints.
            </p>
            <p className="mt-4">
              Due to the periodic nature of data refreshes, records displayed on the Service may not
              reflect real-time changes, including but not limited to:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2 mt-4">
              <li>Address changes or relocations</li>
              <li>Risk level reclassifications</li>
              <li>Record removals or expungements</li>
              <li>Newly registered individuals</li>
              <li>Changes to offense details or compliance status</li>
            </ul>
            <p className="mt-4">
              We make no guarantees regarding the frequency or timing of data refreshes and reserve
              the right to modify the refresh schedule at any time.
            </p>
          </div>

          {/* Section 8 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">8. User Accounts</h2>
            <p>
              Certain features of the Service, including API access and saved searches, may require
              you to create an account. By creating an account, you agree to:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2 mt-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security and confidentiality of your account credentials</li>
              <li>Accept responsibility for all activity that occurs under your account</li>
              <li>Promptly notify us of any unauthorized use of your account</li>
            </ul>
            <p className="mt-4">
              We reserve the right to suspend or terminate any account that we reasonably believe
              has violated these Terms, has been used for unlawful purposes, or has engaged in
              activity detrimental to the Service or its users.
            </p>
          </div>

          {/* Section 9 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">9. Privacy</h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy, which describes how we
              collect, use, and protect your personal information. By using the Service, you consent
              to the practices described in the Privacy Policy.
            </p>
            <p className="mt-4">
              Please review our{' '}
              <Link to="/privacy" className="text-emerald-500 hover:text-amber-400">
                Privacy Policy
              </Link>{' '}
              for complete details on our data handling practices.
            </p>
          </div>

          {/* Section 10 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">10. Changes to These Terms</h2>
            <p>
              Asan Digital LLC reserves the right to modify, amend, or update these Terms at any
              time and at our sole discretion. When we make material changes, we will update the
              "Last Updated" date at the top of this page.
            </p>
            <p className="mt-4">
              Your continued use of the Service following the posting of revised Terms constitutes
              your acceptance of and agreement to the updated Terms. If you do not agree to the
              revised Terms, you must discontinue use of the Service.
            </p>
          </div>

          {/* Section 11 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              State of Delaware, without regard to its conflict of law provisions. Any legal action
              or proceeding arising under these Terms shall be brought exclusively in the federal or
              state courts located in the State of Delaware, and you hereby consent to the personal
              jurisdiction and venue of such courts.
            </p>
          </div>

          {/* Section 12 */}
          <div className="bg-slate-900/50 rounded-xl p-6 mb-6 border border-slate-700/50">
            <h2 className="text-2xl font-semibold text-emerald-500 mt-0 mb-4">12. Contact Information</h2>
            <p>
              If you have any questions, concerns, or requests regarding these Terms of Service,
              please contact us at:
            </p>
            <p className="mt-4">
              <strong>Asan Digital LLC</strong><br />
              Email:{' '}
              <a
                href="mailto:info@asan.digital"
                className="text-emerald-500 hover:text-amber-400"
              >
                info@asan.digital
              </a>
            </p>
          </div>

          {/* Footer note */}
          <p className="mt-10 border-t border-slate-700 pt-6 text-sm text-slate-400 text-center">
            By using CommunityGuardAPI, you acknowledge that you have read, understood, and agree to
            be bound by these Terms of Service.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsPage;
